import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {getSizedImageUrl} from '@shopify/theme-images';

const selectors = {
  widget: '[data-widget="cart-bab-also-bab"]',
  recommendationImageWrapper: '[data-recommendation-image-wrapper]',
  recommendationVariants: '[data-recommendation-variants]',
};

const classes = {
  activeVariantsContainer: 'side-cart__recommendation-variants--active',
  variantSelected: 'is-selected',
};

const CartTagalysRecommendations = ({widgetProductIds, isCartPage}) => {
  const initsRecommendationsWidget = () => {
    const widgetElement = document.querySelector(selectors.widget);
    const widgetName = window.theme?.tagalys?.cartWidgetName ?? 'BoughtAlsoBought';
    const widgetId = widgetName === 'BoughtAlsoBought' ? '' : window.theme?.tagalys?.cartWidgetId;
    const recommendationId = widgetName === 'Recommendations' ? window.theme?.tagalys?.cartWidgetId : '';
    const productId =
      widgetName === 'SmartWidget'
        ? ''
        : window?.theme?.tagalys?.cartWidgetProductIds || widgetProductIds.slice(0, 3).join(',');

    if ((widgetName === 'SmartWidget' || widgetName === 'Recommendations') && !widgetId) return;

    const sliderOptions = isCartPage
      ? {
          300: 2.25,
          480: 4.55,
        }
      : {
          300: 2.25,
          768: 3,
        };

    let slidesCount = null;
    let sliderPrevBtn = null;
    let sliderNextBtn = null;

    window.Tagalys?.UIWidgets[widgetName].init(selectors.widget, {
      widgetId: widgetId,
      recommendationId: recommendationId,
      productId: productId,
      templates: {
        widget: {
          labels: {
            heading: window.theme?.strings?.cartRecommendations?.title || 'You may also like',
          },
          options: {
            limit: 8,
            displayFormat: 'carousel',
            includeTagalysCSS: false,
            carouselOptions: {
              perPage: sliderOptions,
              onChange: function (siemaInstance) {
                const currentSlide = siemaInstance.currentSlide;
                const totalSlides = siemaInstance.innerElements.length;
                const slidesPerPage = siemaInstance.perPage;
                const isLastSlide = siemaInstance.currentSlide >= totalSlides - slidesPerPage;

                sliderPrevBtn?.classList.toggle('hide', currentSlide === 0);
                sliderNextBtn?.classList.toggle('hide', isLastSlide);
              },
            },
          },

          render: function (html, args) {
            const props = args.props;
            const helpers = props.helpers;
            const products = helpers.getProducts()?.filter((product) => product.available);

            slidesCount = products.length;

            if (props.loading === false) {
              if (products.length === 0) {
                return null;
              }
              return html`
                <div class="side-cart__recommendations-container side-cart__section">
                  <${args.templates.header} />
                  <${args.templates.productList} />
                </div>
              `;
            }
          },
        },

        header: {
          render: (html, args) => html`
            <h4 class="side-cart__recommendations-title">${args.labels.heading.replace('&#39;', "'")}</h4>
          `,
        },

        productList: {
          render: (html, args) => html`
            <div class="side-cart__recommendations-wrapper clearfix">
              <${args.templates.products} />
              <${args.templates.carouselActions} />
            </div>
          `,
        },

        products: {
          render: (html, args) => {
            const props = args.props;

            const helpers = props.helpers;
            const products = helpers.getProducts()?.filter((product) => product.available);

            return html`
              <div class="side-cart__recommendations-slider products products-carousel">
                ${products.map((product, index) => html`<${args.templates.product} key=${index} product=${product} />`)}
              </div>
            `;
          },
        },

        product: {
          render: (html, args) => {
            const props = args.props;

            const product = props.product;
            const helpers = props.helpers;

            const productLink = product.__tagalys_fields.link;
            const featuredImage = product.images[0];
            const productPrice = helpers.getPrice ? helpers.getPrice(product) : product.price;
            const productCompareAtPrice = helpers.getCompareAtPrice
              ? helpers.getCompareAtPrice(product)
              : product.compare_at_price;

            // Handle variants toggle and add to cart
            const hasOnlyOneVariant = product.has_only_default_variant || product.variants.length === 1;
            const addButtonLabel = window.theme?.strings?.cartRecommendations?.add || 'Add +';
            const cancelButtonLabel = window.theme?.strings?.cartRecommendations?.cancel || 'Cancel';

            const sizeOptionPosition = product.options_with_values.find(
              (option) => option.name.toLowerCase() === 'size'
            )?.position;

            const handleAddToCart = (event) => {
              event.preventDefault();

              const target = event.target;
              target.disabled = true;
              target.classList.add(classes.variantSelected);

              const variantId = target.dataset.variantId;
              if (!variantId) return;

              const item = {
                id: variantId,
                quantity: 1,
              };
              window.sideCart.handleAddToCartEvent(item, false, () => {
                target.disabled = false;
                target.classList.remove(classes.variantSelected);
              });
            };

            const handleVariantToggle = (event) => {
              event.preventDefault();
              const target = event.target;

              // Get the current variants container
              const currentVariantsContainer =
                target.parentNode.querySelector(selectors.recommendationVariants) ??
                target.closest(selectors.recommendationVariants);

              // Toggle the current variants container
              currentVariantsContainer?.classList.toggle(classes.activeVariantsContainer);

              // Find all active variant containers and close them
              const activeVariantsContainer = widgetElement?.querySelectorAll(`.${classes.activeVariantsContainer}`);
              activeVariantsContainer?.forEach((variantsContainer) => {
                if (variantsContainer === currentVariantsContainer) return;
                variantsContainer.classList.remove(classes.activeVariantsContainer);
              });
            };

            return html`<div class="side-cart__recommendation">
              <div class="side-cart__recommendation-image-wrapper" data-recommendation-image-wrapper>
                <a class="side-cart__recommendation-image" href=${productLink}>
                  <div class="component-image__wrapper aspect-ratio aspect-ratio--portrait">
                    ${featuredImage ? (
                      <img
                        src={getSizedImageUrl(featuredImage.src, '230x')}
                        width={featuredImage.width}
                        height={featuredImage.height}
                        alt={featuredImage.alt ?? product.title}
                      />
                    ) : (
                      ''
                    )}
                  </div>
                </a>

                ${hasOnlyOneVariant
                  ? html`<button
                      class="side-cart__recommendation-btn"
                      type="button"
                      disabled=${!product.available}
                      data-variant-id=${product.variants[0].id}
                      onClick=${handleAddToCart}
                    >
                      ${addButtonLabel}
                    </button>`
                  : html`<button
                        class="side-cart__recommendation-btn"
                        type="button"
                        onClick=${handleVariantToggle}
                        disabled=${!product.available}
                      >
                        ${addButtonLabel}
                      </button>
                      <div class="side-cart__recommendation-variants" data-recommendation-variants>
                        <ul>
                          ${product.variants.map(
                            (variant) =>
                              html`<li>
                                <button
                                  class="side-cart__variant-btn"
                                  type="button"
                                  data-variant-id=${variant.id}
                                  disabled=${!variant.available}
                                  onClick=${handleAddToCart}
                                >
                                  ${sizeOptionPosition ? variant[`option${sizeOptionPosition}`] : variant.title}
                                </button>
                              </li>`
                          )}
                        </ul>
                        <button class="side-cart__variant-toggle" type="button" onClick=${handleVariantToggle}>
                          ${cancelButtonLabel}
                        </button>
                      </div> `}
              </div>

              <h4 class="side-cart__recommendation-title text--small">
                <a href="${productLink}">${product.title}</a>
              </h4>

              <div class="side-cart__recommendation-price">
                <a href="${productLink}">
                  ${helpers.isProductPricesLoading && helpers.isProductPricesLoading()
                    ? ''
                    : html`
                        ${product.price_varies
                          ? html`
                              <span
                                >${window.theme?.strings?.product?.priceFrom?.replace(
                                  '{{ price }}',
                                  helpers.formatCurrency(product.price_min)
                                )}
                              </span>
                            `
                          : html`
                              ${helpers.isProductOnSale(productCompareAtPrice, productPrice)
                                ? html`
                                    <span class="visually-hidden">${window.theme?.strings?.product?.regularPrice}</span>
                                    <s data-compare-price>${helpers.formatCurrency(productCompareAtPrice)}</s>
                                  `
                                : ''}
                              <span
                                data-product-price
                                ${helpers.isProductOnSale(productCompareAtPrice, productPrice)
                                  ? 'class="product-card__sale-price"'
                                  : ''}
                              >
                                ${helpers.formatCurrency(productPrice)}
                              </span>
                            `}
                      `}
                </a>
              </div>
            </div>`;
          },
        },
        carouselActions: {
          render: function (html, args) {
            const props = args.props;

            const getNextButtonClass = () => {
              if (!slidesCount) return '';
              if (slidesCount <= 2) return 'hide';
              if (slidesCount <= 3) return 'hide--min-md';
            };

            return html`
              <button
                class="side-cart__recommendation-prev btn-none hide"
                disabled=${props.helpers.carousel.isFirstSlide()}
                onClick=${props.helpers.carousel.goLeft}
              >
                <svg
                  aria-label="Chevron left icon"
                  class="icon icon-chevron-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m14 18-6-6 6-6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="square"
                    vector-effect="non-scaling-stroke"
                  />
                </svg>
              </button>

              <button
                class="side-cart__recommendation-next btn-none ${getNextButtonClass()}"
                disabled=${props.helpers.carousel.isLastSlide()}
                onClick=${props.helpers.carousel.goRight}
              >
                <svg
                  aria-label="Chevron right icon"
                  class="icon icon-chevron-right"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m10 18 6-6-6-6"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="square"
                    vector-effect="non-scaling-stroke"
                  />
                </svg>
              </button>
            `;
          },
          callbacks: {
            afterInitialRender: function () {
              sliderPrevBtn = document.querySelector('.side-cart__recommendation-prev');
              sliderNextBtn = document.querySelector('.side-cart__recommendation-next');
            },
          },
        },
      },
      callbacks: {
        afterInitialRender: function () {
          // Close any open variant containers if click was outside
          document.addEventListener('click', (event) => {
            const activeVariantsContainers = widgetElement.querySelectorAll(`.${classes.activeVariantsContainer}`);
            if (activeVariantsContainers.length === 0) return;

            const isVariantsContainer = event.target.closest(selectors.recommendationImageWrapper);
            if (isVariantsContainer) return;

            activeVariantsContainers?.forEach((variantContainer) => {
              variantContainer.classList.remove(classes.activeVariantsContainer);
            });
          });
        },
      },
    });
  };

  useEffect(() => {
    onTagalysReady(() => {
      initsRecommendationsWidget();
    });
  }, [widgetProductIds]);

  return <div className="side-cart__recommendations" data-widget="cart-bab-also-bab" />;
};

CartTagalysRecommendations.propTypes = {
  widgetProductIds: PropTypes.arrayOf(PropTypes.number),
  isCartPage: PropTypes.bool,
};

export default CartTagalysRecommendations;
